import React, { useEffect, useRef } from "react";
import {
  I2cRadioButton,
  I2cRadioGroup,
  I2cFileUpload,
  I2cDatePicker,
  I2cIcon,
  I2cButton,
} from "@webcomponents/i2cwebcomponents/dist/react";
import "../Prepaid.scss";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import ErrorMessage from "../../../Components/SharedComponent/ErrorMessage/ErrorMessage";
import FilesUploadService from "../../../Services/FilesUploadService";
import { useMutation } from "react-query";
import { useParams } from "react-router-dom";
const SSRP = ({
  setAddFormData,
  addFormData,
  setFormValidStates,
  formDataJson,
  isFetching,
}) => {
  const nightlyFiles = ["Yes", "No"];
  const Manual = useRef(null);
  const generalErrorMessage = "Field is required";
  const validationSchema = Yup.object({
    ov_nf_ssrp_for_manager: Yup.string()
      .required(generalErrorMessage)
      .min(0, generalErrorMessage),
    ov_nf_cut_off_time: Yup.string().optional(),
    ov_nf_pci_compliant: Yup.string()
      .required(generalErrorMessage)
      .min(1, generalErrorMessage),
    ov_nf_comp_cert: Yup.string().when("ov_nf_pci_compliant", {
      is: (code) => code && code === "yes",
      then: () =>
        Yup.string()
          .required("Please select a compliance certificate")
          .min(1, "Compliance certificate is required."),
      otherwise: () => Yup.string().optional(),
    }),
  });

  // Initialize React Hook Form with Yup resolver
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    mode: "all",
    reValidateMode: "onChange",
    shouldUseNativeValidation: false,
    criteriaMode: "all",
    defaultValues: {
      ov_nf_ssrp_for_manager: "",
      ov_nf_cut_off_time: "00:00:00",
      ov_nf_pci_compliant: "",
      ov_nf_comp_cert: "",
    },
  });
  const formData = new FormData();

  const handleFileUpload = async (name) => {
    try {
      formData.append("file", Manual.current.fileUpload.files[0]);
      await fileUpload.mutate(formData);
    } catch (error) {
      console.log(error);
    } finally {
    }
  };
  const fileUpload = useMutation({
    mutationFn: async (name) => {
      return await FilesUploadService.uploadFile(name);
    },
    onSuccess: (data) => {
      methods.setValue("ov_nf_comp_cert", data?.data?.url);
      methods.trigger();
      handleValueChanges();
    },
    onError: (e) => {
      console.log("api error");
    },
  });
  const downloadFile = async (fileName) => {
    try {
      await fileDownload.mutate(fileName);
    } catch (error) {
      console.log(error);
    } finally {
    }
  };
  const fileDownload = useMutation({
    mutationFn: async (fileName) => {
      return await FilesUploadService.downloadFile(fileName);
    },
    onSuccess: (data) => {
      const url = window.URL.createObjectURL(new Blob([data.data]));
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";

      // Set the download attribute with the file name
      // link.setAttribute('download', fileName);

      // Append to the document and trigger the download
      document.body.appendChild(link);
      link.click();

      // Clean up and remove the link
      link.parentNode.removeChild(link);
    },
    onError: (e) => {
      console.log("api error");
    },
  });
  // Controlls destructuring from the method
  const {
    control,
    formState: { errors },
  } = methods;

  const fields = [
    "ov_nf_ssrp_for_manager",
    "ov_nf_cut_off_time",
    "ov_nf_pci_compliant",
    "ov_nf_comp_cert",
  ];
  const { id: formId } = useParams();
  useEffect(() => {
    if (!isFetching && Object.keys(formDataJson ?? {}).length) {
      fields.forEach((field) => {
        methods.setValue(field, formDataJson[6]["6_8"][field] ?? "");
      });
      methods.trigger();
    }
  }, [isFetching]);

  // Trigger validation when component mounts
  React.useEffect(() => {
    methods.trigger();
  }, []);

  const handleValueChanges = () => {
    const values = methods.getValues();
    setAddFormData({
      ...addFormData,
      6: {
        ...addFormData["6"],
        "6_8": { ...values },
      },
    });
  };
  // Update form validation state when errors change
  React.useEffect(() => {
    const errorsLength = Object.keys(errors).length;
    if (errorsLength === 0) setFormValidStates("6_8", true);
    else setFormValidStates("6_8", false);
  }, [Object.keys(errors).length]);

  return (
    <React.Fragment>
      <div className="forms-instance">
        <div className="section-space">
          <div className="sub-heading mb-16">
            Would program manager also require SSRP nightly files form i2c Inc?
          </div>
          <div className="single-field-wrapper">
            <Controller
              name="ov_nf_ssrp_for_manager"
              control={control}
              render={({ field }) => (
                <I2cRadioGroup
                  value={methods.watch("ov_nf_ssrp_for_manager")}
                  {...field}
                  name="ov_nf_ssrp_for_manager"
                  onI2cChange={(e) => {
                    field.onChange(e);
                    methods.trigger();
                    handleValueChanges();
                  }}
                >
                  {nightlyFiles.map((radio, index) => (
                    <I2cRadioButton
                      key={index}
                      value={radio.toLocaleLowerCase()}
                    >
                      {radio}
                    </I2cRadioButton>
                  ))}
                </I2cRadioGroup>
              )}
            />
          </div>
        </div>
        <div className="halfWidth section-space">
          <div className="sub-heading mb-16">Preferred Cut Off Time</div>
          <div className="single-field-wrapper">
            <Controller
              name="ov_nf_cut_off_time"
              control={control}
              render={({ field }) => (
                <I2cDatePicker
                  className="datePicker0"
                  dateFormat="H:i:s"
                  time_24hr={true}
                  enableTime
                  minuteIncrement={1}
                  placeholder="Select Time"
                  noCalendar
                  size="large"
                  onI2cDateApply={(e) => {
                    if (e.target.appliedDate.length === 0) {
                      field.onChange("00:00:00");
                    } else if (e.target?.appliedDate) {
                      const value = e.target.appliedDate[0]
                        ?.toTimeString()
                        .split(" ")[0];
                      const valuelength = value?.length;
                      field.onChange(valuelength === 7 ? "0" + value : value);
                    } else field.onChange("");
                    handleValueChanges();
                  }}
                  defaultDate={
                    methods.watch("ov_nf_cut_off_time") ??
                    methods.watch("ov_nf_cut_off_time")
                  }
                  suffix='<i2c-icon name="linear-clock-1" slot="suffix"></i2c-icon>'
                />
              )}
            />
            <ErrorMessage
              show={
                errors.ov_nf_cut_off_time &&
                methods.formState.touchedFields.ov_nf_cut_off_time
                  ? true
                  : false
              }
              message={errors.ov_nf_cut_off_time?.message ?? "Invalid input"}
            />
          </div>
        </div>
        <div>
          <div className="sub-heading mb-16">Are you PCI Compliant?</div>
          <div className="single-field-wrapper">
            <Controller
              name="ov_nf_pci_compliant"
              control={control}
              render={({ field }) => (
                <I2cRadioGroup
                  value={methods.watch("ov_nf_pci_compliant")}
                  {...field}
                  name="ov_nf_pci_compliant"
                  onI2cChange={(e) => {
                    if (e.target.value === "no") {
                      methods.setValue("ov_nf_comp_cert", "");
                    }
                    field.onChange(e);
                    methods.trigger();
                    handleValueChanges();
                  }}
                >
                  {nightlyFiles.map((radio, index) => (
                    <I2cRadioButton
                      key={index}
                      value={radio.toLocaleLowerCase()}
                    >
                      {radio}
                    </I2cRadioButton>
                  ))}
                </I2cRadioGroup>
              )}
            />
          </div>
        </div>
        <div className="mt-16">
          {methods.watch("ov_nf_pci_compliant") === "yes" && (
            <>
              <div>
                <div className="mb-16 custom-label-light">
                  Please upload PCI Compliance certificate.
                </div>
                <I2cFileUpload
                  ref={Manual}
                  autoProcessQueue={false}
                  no-mobile-controls
                  max-files={1}
                  className="customFileUpload"
                  setAcceptedFiles=".pdf"
                  icon="linear-document-upload"
                  label="Click to upload"
                  caption="or drag and drop"
                  name="ov_nf_comp_cert"
                  message="Only PDF file (max. 5MB)."
                  onI2cFileRemoved={(e) => {
                    methods.setValue("ov_nf_comp_cert", "");
                    methods.trigger();
                    handleValueChanges();
                  }}
                  onI2cAddedFile={(e) => {
                    handleFileUpload(
                      Manual.current.getDropZone().files[
                        Manual.current.getDropZone().files.length - 1
                      ],
                    );
                  }}
                />
              </div>
              {Object.keys(formDataJson ?? {}).length ? (
                <>
                  {formDataJson[6]["6_8"]?.ov_nf_comp_cert !== null && (
                    <div className="mt-16">
                      <I2cButton
                        size="small"
                        variant="link color"
                        className=""
                        onClick={() => {
                          downloadFile(formDataJson[6]["6_8"]?.ov_nf_comp_cert);
                        }}
                      >
                        <I2cIcon
                          slot="prefix"
                          name="linear-document-download"
                          label="add"
                        ></I2cIcon>{" "}
                        Download File
                      </I2cButton>
                    </div>
                  )}
                </>
              ) : null}
            </>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default SSRP;
